import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import AnDateField from '../../../../components/DateTime/DateField'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import SearchIcon from '@mui/icons-material/Search'
import { PayrollContext } from './Payroll'
import { Grid } from '@mui/material'
import StaffSelector from '../../../events/StaffSelector'
import EmployeeSelector from '../../../events/EmployeeSelector'
import {
  CurrencyPound,
  EuroSymbol,
  FilterAltRounded,
  HowToReg,
  PriceCheck,
  RestartAltRounded,
} from '@mui/icons-material'
import CalendarIcon from '@mui/icons-material/CalendarMonthOutlined'
import WorkIcon from '@mui/icons-material/Work'
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded'
import DateRangeSelector from '../../../../components/DateTime/DateRangeSelector'
import { TenantSettingsContext } from '../../../../App'
import SearchResults from './SearchResults'

const FilterField = ({ icon, label, children, ...props }) => (
  <Stack spacing={0.75} sx={{ flex: 1, minWidth: 220, ...props }}>
    <Stack direction="row" spacing={0.75} alignItems="center">
      {icon}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 900,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
    </Stack>
    {children}
  </Stack>
)

const SearchBar = ({}) => {
  const { searchParams, setSearchParams, defaultPayrollSearchParams } =
    useContext(PayrollContext)

  const { orgSettings } = useContext(TenantSettingsContext)?.orgConfig

  const resetFilters = () => {
    setSearchParams(defaultPayrollSearchParams)
  }

  const hasFilters = !!searchParams?.employee || !!searchParams?.paid

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 4,
        bgcolor: 'background.paper',
        boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap={1}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.main',
                color: '#fff',
              }}
            >
              <FilterAltRounded />
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={950}>
                Filter shift
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Refine shifts by shift type, employee, or paid status.
              </Typography>
            </Box>
          </Stack>

          <Button
            size="small"
            variant="outlined"
            startIcon={<RestartAltRounded />}
            onClick={resetFilters}
            disabled={!hasFilters}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Reset
          </Button>
        </Stack>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          alignItems="flex-start"
        >
          <FilterField
            label="Date Range"
            icon={<CalendarIcon fontSize="small" color="action" />}
            flex={0.4}
            minWidth={280}
          >
            <DateRangeSelector
              value={{
                startDate: searchParams.startDate,
                endDate: searchParams.endDate,
              }}
              onChange={(value) => {
                setSearchParams((prev) => ({
                  ...prev,
                  startDate: value.startDate,
                  endDate: value.endDate,
                }))
              }}
            />
          </FilterField>

          <FilterField
            label="Shift Type"
            icon={<WorkIcon fontSize="small" color="action" />}
            flex={0.3}
            minWidth={140}
          >
            <Autocomplete
              options={['All', 'PTS', 'Event', 'Frontline', 'Admin']}
              value={searchParams.shiftType ?? 'All'}
              disableClearable
              onChange={(e, newValue) =>
                setSearchParams((prev) => ({ ...prev, shiftType: newValue }))
              }
              renderInput={(params) => <TextField {...params} />}
            />
          </FilterField>

          <FilterField
            label="Employee"
            icon={<PersonSearchRoundedIcon fontSize="small" color="action" />}
            flex={1}
            minWidth={340}
          >
            <EmployeeSelector
              value={searchParams?.employee}
              onChange={(e, newValue) =>
                setSearchParams((prev) => ({ ...prev, employee: newValue }))
              }
              label=""
              fullWidth
              shrinkLabel
            />
          </FilterField>
          <FilterField
            label="Paid Status"
            icon={
              orgSettings?.currency === '£' ? (
                <CurrencyPound fontSize="small" color="action" />
              ) : orgSettings?.currency === '€' ? (
                <EuroSymbol fontSize="small" color="action" />
              ) : (
                <HowToReg fontSize="small" color="action" />
              )
            }
            minWidth={180}
            flex={0.3}
          >
            <Autocomplete
              options={['All', 'Unpaid', 'Paid']}
              value={searchParams.paid}
              onChange={(e, newValue) =>
                setSearchParams((prev) => ({
                  ...prev,
                  paid: newValue,
                }))
              }
              renderInput={(params) => <TextField {...params} />}
              disableClearable
            />
          </FilterField>

          {/* TODO: BASE LOCATIONS NOT CURRENTLY PART OF THE SHIFT SCHEMA. EITHER CREATE A VIRTUAL OR REFERENCE THE PTS DUTY / EVENT */}
          {/* <FilterField
            label="Base Locations"
            icon={<BusinessRoundedIcon fontSize="small" color="action" />}
          >
            <BaseLocationPicker
              selected={filter?.baseLocations}
              multiple
              onChange={(baseLocations) =>
                setFilter((prev) => ({
                  ...prev,
                  baseLocations,
                }))
              }
            />
          </FilterField> */}
        </Stack>
        <SearchResults />
      </Stack>
    </Paper>
  )
}

export default SearchBar
